<script setup lang="ts">
import { computed, nextTick, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import BrandColors from '@pocketprep/ui-kit/pocketprep-export.module.scss'
import TableHeader from '@/components/TableHeader.vue'
import AddGroupSidePanel from '@/components/SidePanels/AddGroup.vue'
import { groupModule } from '@/store/group/module'
import { examModule } from '@/store/exam/module'
import { instructorModule } from '@/store/instructor/module'
import { studentModule } from '@/store/student/module'
import { statsModule } from '@/store/stats/module'
import UIKit, {
    type ITableSortSettings,
    type ITableAction,
    type ITableColumn,
    type TTableRow,
} from '@pocketprep/ui-kit'
import { analyticsModule } from '@/store/analytics/module'
import { organizationModule } from '@/store/organization/module'

export interface IMappedGroup extends TTableRow {
    id: string
    createdAt: Date
    name: string
    numStudents: number
    selected: boolean
    examGuid: string
    styles?: Record<string, string>
}

const {
    Table,
    TableActions,
    Icon,
    OverflowTooltip,
    Tooltip,
} = UIKit

const route = useRoute()
const router = useRouter()

const rowRefs = ref<Record<string, HTMLElement>>({})

const setRowRef = (id: string, el: unknown) => {
    if (el) {
        rowRefs.value[id] = el as HTMLElement
    } else {
        delete rowRefs.value[id]
    }
}

const showAddGroupSidePanel = ref(false)
const showDragTooltip = ref(false)
const customOrderedGroups = ref<IMappedGroup[] | null>(null)
const isDragging = ref(false)
const isAbleToDrag = ref(false)
const rowHoverId = ref<string | null>(null)
const draggingRowIndex = ref(-1)
const draggingOverIndex = ref(-1)
const draggingOverRowTop = ref(false)

const groupActions: ITableAction[] = [{
    id: 1,
    icon: 'add',
    text: 'Add Group',
    menu: [{
        id: 3,
        text: 'Create New Group',
    }, {
        id: 4,
        text: 'Browse Groups',
    }],
}]

const groupColumns: ITableColumn[] = [{
    name: 'Group Name',
    propName: 'name',
}, {
    name: 'No. of Students',
    propName: 'numStudents',
    styles: {
        textAlign: 'right',
    },
}]

const sortSettings = ref<ITableSortSettings>({ column: groupColumns[1], direction: 0 })

const visibleGroups = computed(() => {
    return groupModule.getters.getCurrentVisibleOrgGroups()
})

const currentGroup = computed(() => {
    return groupModule.getters.getCurrentGroup()
})

const currentStudentId = computed(() => {
    return studentModule.state.currentStudentId
})

const currentOrgGroups = computed(() => {
    return groupModule.getters.getCurrentOrgGroups()
})

const currentOrgInstructors = computed(() => {
    return instructorModule.getters.getCurrentOrgInstructors()
})

const currentOrgInstructorUsers = computed(() => {
    return instructorModule.getters.getCurrentOrgInstructorUsers()
})

const currentOrganization = computed(() => {
    return organizationModule.getters.getCurrentOrganization()
})

const mappedGroups = computed(() => {
    const sortSettingsVal = sortSettings.value
    return Object.values(visibleGroups.value.reduce((acc, g) => {
        const currentGroupVal = currentGroup.value
        if (!currentGroupVal) {
            return acc
        }
        let groupName = currentOrgGroups.value[g.objectId]?.name || ''
        const owner = currentOrgGroups.value[g.objectId]?.owner?.objectId
        if (g.type === 'system' && owner && currentOrgInstructors.value && currentOrgInstructorUsers.value) {
            const ownerOrgInstructor = currentOrgInstructors.value[owner]
            if (ownerOrgInstructor) {
                const user = currentOrgInstructorUsers.value[ownerOrgInstructor.user.objectId]
                if (user?.firstName && user?.lastName) {
                    const name = [ user.firstName, user.lastName.slice(0, 1) ].join(' ')
                    groupName += ` - ${name}`
                }
            }
        }
        const examGuid = examModule.getters.getExamGuidForOrgGroupId(g.objectId) || ''
        const groupCompositeKey = groupModule.getters.getGroupCompositeKeyForGroupId(g.objectId)
        const currentGroupCompositeKey = groupModule.getters.getGroupCompositeKeyForGroupId(currentGroupVal.objectId)

        const mappedGroup: IMappedGroup = {
            id: g.objectId,
            createdAt: new Date(g.createdAt),
            name: groupName,
            numStudents: currentOrgGroups.value[g.objectId]?.orgStudents.length || 0,
            selected: false,
            examGuid,
        }

        if (
            !currentStudentId.value
            && groupCompositeKey === currentGroupCompositeKey
        ) {
            mappedGroup.selected = true
            mappedGroup.styles = {
                backgroundColor: 'rgba(255, 249, 239)',
                fontWeight: '600',
            }
        } else if (mappedGroup.id === rowHoverId.value) {
            mappedGroup.styles = {
                backgroundColor: `${BrandColors.grayBackground}60`,
            }
        }

        if (groupCompositeKey && !acc[groupCompositeKey]) {
            acc[groupCompositeKey] = mappedGroup
        }
        return acc
    }, {} as { [ guidOwner: string ]: IMappedGroup }))
        .sort((a, b) => {
            if (sortSettingsVal?.column) {
                if (sortSettingsVal.column.name === 'Group Name') {
                    return sortSettingsVal.direction * a.name.localeCompare(b.name)
                } else if (sortSettingsVal.column.name === 'No. of Students') {
                    return sortSettingsVal.direction * (b.numStudents - a.numStudents)
                }
            }
            return 0
        })
})

const groupRows = computed(() => {
    return customOrderedGroups.value || mappedGroups.value
})

const hideDragTooltip = () => {
    showDragTooltip.value = false
}

const setAbleToDrag = (value: boolean) => {
    isAbleToDrag.value = value
}

const handleMouseEnterOnDragHandle = (id: string) => {
    if (!isDraggingOver(id)) {
        showDragTooltip.value = true
    }
}

const isDraggingRow = (id: string) => {
    return mappedGroups.value.findIndex(g => g.id === id) === draggingRowIndex.value
}

const isDraggingOver = (id: string) => {
    return mappedGroups.value.findIndex(g => g.id === id) === draggingOverIndex.value
}

const dragStart = (id: string, event: DragEvent) => {
    showDragTooltip.value = false
    if (!isAbleToDrag.value) {
        event.preventDefault()
        return
    }
    if (event.dataTransfer) {
        event.dataTransfer.effectAllowed = 'move'
        event.dataTransfer.dropEffect = 'none'
    }
    requestAnimationFrame(() => {
        draggingRowIndex.value = mappedGroups.value.findIndex(g => g.id === id)
    })
}

const dragEnter = (id: string, event: DragEvent) => {
    isDragging.value = true
    draggingOverIndex.value = mappedGroups.value.findIndex(g => g.id === id)
    const cursorY = event.clientY
    const rowEl = rowRefs.value[id]
    if (rowEl) {
        const rowElMiddle = rowEl.getBoundingClientRect().top + (rowEl.clientHeight / 2)
        draggingOverRowTop.value = cursorY < rowElMiddle
    }
}

const dragEnd = () => {
    draggingOverIndex.value = -1
    draggingRowIndex.value = -1
    isAbleToDrag.value = false
    isDragging.value = false
}

const dragFinish = (id: string) => {
    const to = mappedGroups.value.findIndex(g => g.id === id)

    updateRowOrder({
        from: draggingRowIndex.value,
        to: to > draggingRowIndex.value
            ? draggingOverRowTop.value
                ? to - 1
                : to
            : draggingOverRowTop.value
                ? to
                : to + 1,
    })
}

const rowEnter = (row: TTableRow) => {
    rowHoverId.value = String(row.id)
}

const rowLeave = (row: TTableRow) => {
    if (row.id === rowHoverId.value) {
        rowHoverId.value = null
    }
}

const selectRow = (rawRowId: string | number) => {
    const rowId = String(rawRowId)
    if (route.params.groupId !== rowId || route.params.studentId) {
        const newGroupCompositeKey = groupModule.getters.getGroupCompositeKeyForGroupId(rowId)
        const newGroup = newGroupCompositeKey
            && groupModule.getters.getCurrentGroupForGroupCompositeKey(newGroupCompositeKey)
        if (!newGroup) {
            return
        }

        if (newGroup && currentGroup.value?.orgExam.objectId !== newGroup.orgExam.objectId) {
            const examMetadataIdForNewGroup = examModule.getters.getExamMetadataForOrgGroupId(
                newGroup.objectId
            )?.objectId
            if (examMetadataIdForNewGroup) {
                examModule.actions.updateCurrentExamMetadataId(examMetadataIdForNewGroup)
            }
            statsModule.actions.resetStudentAggStats()
            statsModule.actions.resetStudentStats()
            statsModule.actions.resetGroupAggStats()
        }

        const routeName = route.name as string

        if (routeName.includes('stats')) {
            router.push({
                name: 'group-stats',
                params: {
                    groupId: newGroup.objectId,
                },
                query: { ...route.query },
            })
        } else if (routeName.includes('content')) {
            router.push({
                name: 'group-content',
                params: {
                    groupId: newGroup.objectId,
                },
            })
        }
    }
}

const rowClicked = (row: TTableRow) => {
    selectRow(row.id)
    analyticsModule.actions.amplitudeTrack('Stats Group Table Clicked', {
        groupName: String(row.name),
        organizationName: currentOrganization.value?.name || '',
    })
}

const groupActionClicked = (action: { text: string }) => {
    if (action.text === 'Create New Group') {
        showAddGroupSidePanel.value = true
    }
    if (action.text === 'Browse Groups') {
        router.push({
            name: 'organization',
            hash: '#groups',
        })
    }
}

const updateRowOrder = async ({ to, from }: { to: number; from: number }) => {
    customOrderedGroups.value = [ ...mappedGroups.value ]
    const [ removed ] = customOrderedGroups.value.splice(from, 1)
    if (removed !== undefined) {
        customOrderedGroups.value.splice(to, 0, removed)
    }
    await instructorModule.actions.updateVisibleOrgGroups(customOrderedGroups.value.map(g => g.id))
    customOrderedGroups.value = null
}

const updateSortSettings = (newSortSettings: ITableSortSettings) => {
    sortSettings.value = newSortSettings
}

const closeSidePanel = () => {
    showAddGroupSidePanel.value = false
}

const scrollGroupRows = () => {
    if (groupRows.value?.length) {
        const selectedGroupRowIndex = groupRows.value.findIndex(groupRow => groupRow.selected)

        nextTick(() => {
            const groupRowEls = document.querySelectorAll('.groups-table tbody.uikit-table__rows .uikit-table__row')
            const selectedGroupRowEl = groupRowEls?.[selectedGroupRowIndex] as HTMLElement | undefined
            const selectedRowOffsetTop = selectedGroupRowEl?.offsetTop

            const groupRowsContainerEl = document.querySelector(
                '.groups-table tbody.uikit-table__rows'
            ) as HTMLElement | undefined
            if (
                groupRowsContainerEl
                && selectedRowOffsetTop
                && selectedRowOffsetTop > groupRowsContainerEl.offsetHeight
            ) {
                groupRowsContainerEl.scrollTop = selectedRowOffsetTop || 0
            }
        })
    }
}

onMounted(async () => {
    await Promise.all([
        groupModule.actions.fetchCurrentVisibleOrgGroups(),
        groupModule.actions.fetchCurrentGroup(),
        examModule.actions.fetchExamMetadata(),
        examModule.actions.fetchCurrentOrgExams(),
        instructorModule.actions.fetchCurrentOrgInstructors(),
        instructorModule.actions.fetchCurrentOrgInstructorUsers(),
        statsModule.actions.fetchCurrentGroupAggStats(),
    ])
    await groupModule.actions.fetchGroupsActiveStudentIds()

    if (!currentStudentId.value && currentGroup.value && route.name !== 'invite') {
        selectRow(currentGroup.value.objectId)
        scrollGroupRows()
    }
    if (visibleGroups.value.length && !visibleGroups.value.find(vg => vg.objectId === currentGroup.value?.objectId)) {
        const firstVisibleGroupId = visibleGroups.value[0]?.objectId
        if (firstVisibleGroupId) {
            selectRow(firstVisibleGroupId)
        }
    }
})
</script>

<template>
    <div>
        <div class="groups-table">
            <TableHeader
                title="Groups"
                :count="groupRows.length"
            >
                <template #tableHeaderRight>
                    <TableActions
                        :actions="groupActions"
                        @actionClicked="groupActionClicked"
                    />
                </template>
            </TableHeader>
            <Table
                class="groups-table__table"
                :default-sort="sortSettings"
                :show-header="false"
                :rows="groupRows"
                :columns="groupColumns"
                :table-container-styles="{
                    borderRadius: '4px',
                }"
                :table-content-styles="{
                    borderWidth: '1px',
                    borderRadius: '4px',
                    borderColor: '#dfe3e7',
                    borderStyle: 'solid',
                    overflow: 'hidden',
                }"
                :table-column-labels-styles="{
                    backgroundColor: '#f4f5f9',
                    padding: '3px 0 3px 15px',
                    height: '25px',
                }"
                :table-rows-styles="{
                    maxHeight: '414px',
                }"
                :table-row-styles="{
                    fontSize: '13px',
                    lineHeight: '13px',
                    cursor: 'pointer',
                    padding: '10px 15px',
                }"
                :table-sort-icon-styles="{
                    marginLeft: '2px',
                    marginRight: '2px'
                }"
                @sort="updateSortSettings"
                @rowEnter="rowEnter"
                @rowLeave="rowLeave"
                @rowClicked="rowClicked"
            >
                <template
                    #tableRow="{ row }"
                >
                    <tr
                        :key="`row--${row.id}`"
                        :ref="(el) => setRowRef(row.id, el)"
                        class="groups-table__draggable-row"
                        :class="{
                            'groups-table__draggable-row--dragging': isDraggingRow(row.id),
                            'groups-table__draggable-row--drop-below': !draggingOverRowTop && isDraggingOver(row.id),
                            'groups-table__draggable-row--drop-above': draggingOverRowTop && isDraggingOver(row.id),
                        }"
                        role="row"
                        :style="{
                            ...row.styles,
                        }"
                        draggable="true"
                        @dragstart="dragStart(row.id, $event)"
                        @dragover.prevent
                        @dragenter.self="dragEnter(row.id, $event)"
                        @dragend="dragEnd"
                        @drop="dragFinish(row.id)"
                        @click="rowClicked(row)"
                        @mouseenter="rowEnter(row)"
                        @mouseleave="rowLeave(row)"
                    >
                        <template v-for="column in groupColumns" :key="`row-${row.id}--column-${column.propName}`">
                            <td
                                role="cell"
                                class="groups-table__draggable-row-cell"
                                :class="{
                                    'groups-table__draggable-row-cell--name': column.propName === 'name',
                                }"
                                :style="{
                                    ...column.styles,
                                }"
                            >
                                <template v-if="column.propName === 'name' && rowHoverId === row.id && !isDragging">
                                    <Tooltip
                                        v-if="showDragTooltip"
                                        class="groups-table__draggable-row-icon-tooltip"
                                        theme="rightalign"
                                    >
                                        Drag to reorder
                                    </Tooltip>
                                    <Icon
                                        class="groups-table__draggable-row-icon"
                                        type="gridDrag"
                                        draggable="true"
                                        @mouseenter="handleMouseEnterOnDragHandle(row.id)"
                                        @mouseleave="hideDragTooltip"
                                        @mousedown="setAbleToDrag(true)"
                                        @mouseup="setAbleToDrag(false)"
                                        @click.stop
                                    />
                                </template>
                                <OverflowTooltip>
                                    {{ row[column.propName] }}
                                </OverflowTooltip>
                            </td>
                        </template>
                    </tr>
                </template>
            </Table>
        </div>
        <AddGroupSidePanel v-if="showAddGroupSidePanel" @close="closeSidePanel" />
    </div>
</template>

<style lang="scss" scoped>
.groups-table {
    :deep(*) {
        .uikit-table__row:last-child {
            border-bottom: none;
        }
        .uikit-table__cell:first-child {
            width: 250px;
        }
    }
    &__draggable-row {
        display: inline-grid;
        grid-template-columns: 250px 1fr;
        min-width: 100%;
        padding: 10px 22px 10px 16px;
        border-bottom: 1px solid $gray-divider;
        font-size: 13px;
        line-height: 13px;
        cursor: pointer;

        &--dragging {
            display: none;
        }

        &--drop-below {
            border-bottom: 37px solid $gray-background;
        }

        &--drop-above {
            border-top: 36px solid $gray-background;
        }
    }

    &__draggable-row-cell {
        white-space: nowrap;
        text-overflow: ellipsis;
        display: block;
        position: relative;

        &:not(:last-child) {
            margin-right: 11px;
        }

        &--name {
            display: flex;
            align-items: center;
        }
    }

    &__draggable-row-icon {
        position: absolute;
        left: -14px;
        top: 1px;
        width: 12px;
        color: $pewter;
        cursor: pointer;

        &:hover {
            color: $brand-blue;
        }
    }

    &__draggable-row-icon-tooltip {
        position: absolute;
        top: -30px;
        left: -8px;
    }
}
</style>
